/**
 * @module components/Checkout/useCheckoutForm
 * @description Custom React hooks and HOC for checkout form state management.
 * Provides form context, field-level hooks, and validation integration.
 */
import React, {
  useMemo,
  useState,
  useEffect,
  useReducer,
  useContext,
  useCallback,
} from 'react';

import formReducer, { reset, getErrorMessage } from './formReducer';
import {
  FormState,
  FormActionType,
  CheckoutFormProps,
  UseFieldReturn,
} from 'src/types/forms';
import type { FormContextValue } from 'src/types/forms-hooks';

/**
 * @const FormContext
 * @description React Context for checkout form state.
 * Provides form values, errors, and dispatch function to child components.
 */
const FormContext = React.createContext<FormContextValue | undefined>(
  undefined,
);

/**
 * @func withCheckoutFormContext
 * @summary React HOC - wraps given `Component` in `FormContext.Provider`, allowing child to access form processing/validation utils
 *  - includes required field processing
 *  - includes form validation
 * @param Component - React component to wrap
 * @returns Component wrapped with `FormContext.Provider`
 */
export const withCheckoutFormContext = <P extends object>(
  Component: React.ComponentType<P & CheckoutFormProps>,
) => {
  return (props: P) => {
  const [form, dispatch] = useReducer(formReducer, {}, reset);

    const [requiredFields, _setRequired] = useState<string[]>([]);

  /**
   * @func setRequiredField
   * @summary React Callback - add/toggle field in `required` fields array via `_setRequired` hooks
     * @param fieldName - Name of field
   */
    const setRequiredField = useCallback((fieldName: string) => {
    _setRequired((fields) =>
        fields.includes(fieldName) ? fields : fields.concat(fieldName),
    );
  }, []);

  /**
   * @func validateForm
   * @summary React Callback - determine if all required fields are valid
     * @returns Whether all fields are valid
   */
  const validateForm = useCallback(() => {
    // force validation of required fields, generating their error messages
    requiredFields.forEach((field) =>
        dispatch({ type: FormActionType.VALIDATE, payload: field }),
    );

      const isValid = (field: string) =>
        !getErrorMessage(field, form?.values[field]);
    return requiredFields.every(isValid);
  }, [requiredFields, form]);

  const init = useCallback(
      (obj: Partial<FormState>) =>
        dispatch({ type: FormActionType.INIT, payload: obj }),
    [],
  );
  const _reset = useCallback(
      (obj: Partial<FormState> = {}) =>
        dispatch({ type: FormActionType.RESET, payload: obj }),
    [],
  );
  const setValue = useCallback(
      (field: string, val: string | number) =>
        dispatch({
          type: FormActionType.SET_VALUE,
          payload: { [field]: val as string },
        }),
    [],
  );

  return (
    <FormContext.Provider value={[form, dispatch, setRequiredField]}>
      <Component
        {...props}
        form={form}
        validate={validateForm}
        setValue={setValue}
        init={init}
        reset={_reset}
      />
    </FormContext.Provider>
  );
  };
};

/**
 * @func useCheckoutForm
 * @summary React Hook - returns the values from the formReducer. The `form` value is stateful, so
 *   it will cause re-renders.
 * @returns `FormContext` context instance
 */
export const useCheckoutForm = (): FormContextValue => {
  const context = useContext(FormContext);
  if (!context) {
  console.error(
    "useCheckoutForm must be used in the context of the CheckoutForm's FormContext.Provider",
  );
    throw new Error(
      "useCheckoutForm must be used in the context of the CheckoutForm's FormContext.Provider",
    );
  }
  return context;
};

/**
 * @func useField
 * @summary React Hook - returns stateful value and error, with a static set of actions
 * @param field - Field name
 * @param isRequired - If field is required
 * @returns Memoized `Field` object
 */
export const useField = (
  field: string,
  isRequired?: boolean,
): UseFieldReturn => {
  const [form, dispatch, setRequiredField] = useCheckoutForm();
  const [error, setError] = useState<string | false | undefined>();
  const [value, setValue] = useState<string | number | undefined>();

  useEffect(() => {
    if (form.errors[field] !== error) {
      setError(form.errors[field]);
    }
    if (form.values[field] !== value) {
      setValue(form.values[field]);
    }
  }, [form, error, value, field]);

  useEffect(() => {
    if (isRequired) {
      setRequiredField(field);
    }
  }, [setRequiredField, field, isRequired]);

  return useMemo(
    () => ({
      setError: (msg: string) => {
        if (msg) {
          dispatch({ type: FormActionType.SET_ERROR, payload: { [field]: msg } });
        }
        return false;
      },
      setValue: (val: string | number) =>
        dispatch({
          type: FormActionType.SET_VALUE,
          payload: { [field]: val as string },
        }),
      unsetError: () =>
        dispatch({ type: FormActionType.UNSET_ERROR, payload: field }),
      validate: () =>
        dispatch({ type: FormActionType.VALIDATE, payload: field }),
      value,
      error,
    }),
    [dispatch, field, value, error],
  );
};
